import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, IconButton, Typography, Box, Chip,
  CircularProgress, useTheme, useMediaQuery, Tooltip,
  TextField, Collapse
} from '@mui/material';
import {
  Close as CloseIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
  Twitter as TwitterIcon,
  CheckCircle as CheckIcon,
  ArrowForward as ArrowIcon,
  Lock as LockIcon,
  Save as SaveIcon,
  EditLocation as PlaceIdIcon
} from '@mui/icons-material';

import { useInitiateOAuthMutation }      from '../../redux/thunks/ai/initiateoauth';
import { useGetSocialAccountsQuery }     from '../../redux/thunks/ai/getsocialaccounts';
import { useSaveGooglePlaceIdMutation }  from '../../redux/thunks/sp/savegoogleplaceid';
import PermissionsConsentDialog          from './PermissionsConsentDialog';

// ===== Platform tile icon =====
const PlatformIcon = ({ id }) => {
  const styles = { fontSize: 28 };
  switch (id) {
    case 'facebook':
    case 'facebook_instagram':
      return (
        <Box sx={{ display: 'flex', gap: 0.25 }}>
          <FacebookIcon sx={{ ...styles, color: '#1877F2' }} />
          <InstagramIcon sx={{ ...styles, color: '#E4405F' }} />
        </Box>
      );
    case 'linkedin':
      return <LinkedInIcon sx={{ ...styles, color: '#0A66C2' }} />;
    case 'twitter':
      return <TwitterIcon sx={{ ...styles, color: '#1DA1F2' }} />;
    case 'google':
      return (
        <Typography fontWeight={800} sx={{ fontSize: 22, color: '#4285F4', lineHeight: 1 }}>
          G
        </Typography>
      );
    case 'yelp':
      return (
        <Typography fontWeight={800} sx={{ fontSize: 18, color: '#D32323', lineHeight: 1 }}>
          y!
        </Typography>
      );
    case 'tripadvisor':
      return (
        <Typography fontWeight={800} sx={{ fontSize: 16, color: '#34E0A1', lineHeight: 1 }}>
          TA
        </Typography>
      );
    case 'trustpilot':
      return (
        <Typography fontWeight={800} sx={{ fontSize: 14, color: '#00B67A', lineHeight: 1 }}>
          TP
        </Typography>
      );
    default:
      return null;
  }
};

// ===== Standard platform tile =====
const PlatformTile = ({ platform, selected, onClick }) => {
  const tile = (
    <Box
      onClick={() => !platform.disabled && !platform.comingSoon && onClick(platform.id)}
      sx={{
        position:       'relative',
        display:        'flex',
        flexDirection:  'column',
        alignItems:     'center',
        justifyContent: 'center',
        gap:            0.75,
        p:              1.5,
        borderRadius:   2,
        border:         '2px solid',
        borderColor:    selected
          ? 'primary.main'
          : platform.comingSoon ? 'divider' : 'grey.200',
        bgcolor:        selected
          ? 'primary.50'
          : platform.comingSoon ? 'grey.50' : 'background.paper',
        cursor:         platform.disabled || platform.comingSoon ? 'default' : 'pointer',
        opacity:        platform.comingSoon ? 0.6 : 1,
        transition:     'all 0.15s',
        minHeight:      90,
        '&:hover':      platform.comingSoon || platform.disabled ? {} : {
          borderColor: selected ? 'primary.main' : 'primary.light',
          boxShadow:   1
        }
      }}
    >
      {platform.isConnected && (
        <CheckIcon sx={{ position: 'absolute', top: 4, right: 4, fontSize: 16, color: 'success.main' }} />
      )}
      {platform.comingSoon && (
        <LockIcon sx={{ position: 'absolute', top: 4, right: 4, fontSize: 14, color: 'text.disabled' }} />
      )}
      <PlatformIcon id={platform.id} />
      <Typography
        variant="caption"
        fontWeight={600}
        textAlign="center"
        color={platform.comingSoon ? 'text.disabled' : 'text.primary'}
        sx={{ lineHeight: 1.2 }}
      >
        {platform.name}
      </Typography>
      {platform.comingSoon && (
        <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>
          Coming Soon
        </Typography>
      )}
      {platform.isConnected && !platform.comingSoon && (
        <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'success.main' }}>
          Connected
        </Typography>
      )}
    </Box>
  );

  return platform.comingSoon
    ? <Tooltip title="Coming soon">{tile}</Tooltip>
    : tile;
};

// ===== Google tile — special: OAuth (coming soon) + Place ID (active) =====
const GoogleTile = ({isConnected, hasPlaceId, onSetPlaceId, onConnectOAuth }) => (
  <Box
    sx={{
      position:      'relative',
      display:       'flex',
      flexDirection: 'column',
      alignItems:    'center',
      gap:           0.75,
      p:             1.5,
      borderRadius:  2,
      border:        '1px solid',
      borderColor:   isConnected ? 'success.light' : 'grey.200',
      bgcolor:       'background.paper',
      minHeight:     90,
      minWidth:      110,
    }}
  >
    {/* ===== Top half — Google OAuth (now active) ===== */}
    <Box
      onClick={onConnectOAuth}
      sx={{
        display:        'flex',
        flexDirection:  'column',
        alignItems:     'center',
        gap:            0.5,
        cursor:         'pointer',
        p:              0.5,
        borderRadius:   1,
        width:          '100%',
        '&:hover':      { bgcolor: 'primary.50' }
      }}
    >
      {isConnected && (
        <CheckIcon sx={{
          position: 'absolute', top: 4, right: 4,
          fontSize: 16, color: 'success.main'
        }} />
      )}
      <PlatformIcon id="google" />
      <Typography variant="caption" fontWeight={600} sx={{ lineHeight: 1.2 }}>
        Google
      </Typography>
      <Typography
        variant="caption"
        sx={{
          fontSize: '0.6rem',
          color:    isConnected ? 'success.main' : 'primary.main',
          fontWeight: 600
        }}
      >
        {isConnected ? 'GBP Connected ✓' : 'Connect GBP'}
      </Typography>
    </Box>

    {/* ===== Divider ===== */}
    <Box sx={{
      width:       '100%',
      borderTop:   '1px dashed',
      borderColor: 'grey.200',
      my:          0.25
    }} />

    {/* ===== Bottom half — Place ID (unchanged) ===== */}
    <Tooltip title="Set your Google Place ID to pull reviews via Outscraper">
      <Box
        onClick={onSetPlaceId}
        sx={{
          display:       'flex',
          flexDirection: 'column',
          alignItems:    'center',
          gap:           0.25,
          cursor:        'pointer',
          p:             0.5,
          borderRadius:  1,
          '&:hover':     { bgcolor: 'primary.50' }
        }}
      >
        <PlaceIdIcon sx={{
          fontSize: 16,
          color:    hasPlaceId ? 'success.main' : 'text.secondary'
        }} />
        <Typography
          variant="caption"
          sx={{
            fontSize:   '0.6rem',
            color:      hasPlaceId ? 'success.main' : 'text.secondary',
            fontWeight: 600
          }}
        >
          {hasPlaceId ? 'Place ID ✓' : 'Set Place ID'}
        </Typography>
      </Box>
    </Tooltip>
  </Box>
);

// ===== Main dialog =====
const OAuthConnectionDialog = ({
  open,
  onClose,
  businessId,
  businessName,
  currentGooglePlaceId = ''   // ← new prop
}) => {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [selectedPlatform,       setSelectedPlatform]       = useState('');
  const [isConnecting,           setIsConnecting]           = useState(false);
  const [showPermissionsConsent, setShowPermissionsConsent] = useState(false);

  // Place ID state
  const [showPlaceIdInput, setShowPlaceIdInput] = useState(false);
  const [googlePlaceId,    setGooglePlaceId]    = useState(currentGooglePlaceId || '');
  const [placeIdSaved,     setPlaceIdSaved]     = useState(false);

  const [initiateOAuth]                         = useInitiateOAuthMutation();
  const { data: socialAccounts }                = useGetSocialAccountsQuery();
  const [saveGooglePlaceId, { isLoading: savingPlaceId }] = useSaveGooglePlaceIdMutation();

  // Sync prop → state when dialog opens
  useEffect(() => {
    if (open) {
      setGooglePlaceId(currentGooglePlaceId || '');
      setPlaceIdSaved(false);
      setShowPlaceIdInput(false);
      setSelectedPlatform('');
    }
  }, [open, currentGooglePlaceId]);

  const isConnected = (platform) =>
    socialAccounts?.accounts?.some(a => a.platform === platform && a.isHealthy);

  const hasPlaceId = !!(googlePlaceId || currentGooglePlaceId);

  const handleSavePlaceId = async () => {
    if (!googlePlaceId.trim() || !businessId) return;
    try {
      await saveGooglePlaceId({
        businessId,
        googlePlaceId: googlePlaceId.trim()
      }).unwrap();
      setPlaceIdSaved(true);
      setTimeout(() => setShowPlaceIdInput(false), 1500);
    } catch (error) {
      console.error('Failed to save Place ID:', error);
    }
  };

  const SOCIAL_PLATFORMS = [
    {
      id:          'facebook_instagram',
      name:        'Facebook & Instagram',
      isConnected: isConnected('facebook') || isConnected('instagram'),
      requiresPermissionConsent: true,
      comingSoon:  false
    },
    {
      id:          'linkedin',
      name:        'LinkedIn',
      isConnected: isConnected('linkedin'),
      requiresPermissionConsent: false,
      comingSoon:  false
    },
    {
      id:          'twitter',
      name:        'Twitter / X',
      isConnected: isConnected('twitter'),
      requiresPermissionConsent: false,
      comingSoon:  true
    },
    {
      id:          'tiktok',
      name:        'TikTok',
      isConnected: false,
      requiresPermissionConsent: false,
      comingSoon:  true
    },
  ];

  const REVIEW_PLATFORMS = [
    {
      id:          'yelp',
      name:        'Yelp',
      isConnected: false,
      requiresPermissionConsent: false,
      comingSoon:  true
    },
    {
      id:          'tripadvisor',
      name:        'Tripadvisor',
      isConnected: false,
      requiresPermissionConsent: false,
      comingSoon:  true
    },
    {
      id:          'trustpilot',
      name:        'Trustpilot',
      isConnected: false,
      requiresPermissionConsent: false,
      comingSoon:  true
    },
  ];

  const allPlatforms = [...SOCIAL_PLATFORMS, ...REVIEW_PLATFORMS];

  const handleConnect = () => {
    if (!selectedPlatform) return;
    const platform = allPlatforms.find(p => p.id === selectedPlatform);
    if (platform?.requiresPermissionConsent) {
      setShowPermissionsConsent(true);
    } else {
      initiateOAuthFlow();
    }
  };

  const handlePermissionsAccepted = () => {
    setShowPermissionsConsent(false);
    initiateOAuthFlow();
  };

  const initiateOAuthFlow = async () => {
    setIsConnecting(true);
    try {
      const oauthPlatform = selectedPlatform === 'facebook_instagram'
        ? 'facebook'
        : selectedPlatform;

      const redirectUri = `${window.location.origin}/oauth/callback/${oauthPlatform}`;
      sessionStorage.setItem('oauth_platform',           oauthPlatform);
      sessionStorage.setItem('oauth_original_selection', selectedPlatform);
      if (businessId && businessName) {
        sessionStorage.setItem('oauth_business_id',   businessId);
        sessionStorage.setItem('oauth_business_name', businessName);
        sessionStorage.setItem('oauth_return_path',   `/business/${businessId}/${businessName}`);
      }

      const result = await initiateOAuth({ platform: oauthPlatform, redirectUri }).unwrap();
      if (result.authUrl) window.location.href = result.authUrl;

    } catch (error) {
      console.error('OAuth initiation failed:', error);
      alert('Failed to initiate connection. Please try again.');
      sessionStorage.removeItem('oauth_platform');
      sessionStorage.removeItem('oauth_original_selection');
      sessionStorage.removeItem('oauth_business_id');
      sessionStorage.removeItem('oauth_business_name');
      sessionStorage.removeItem('oauth_return_path');
    } finally {
      setIsConnecting(false);
      onClose();
    }
  };

  const PlatformGroup = ({ title, platforms }) => (
    <Box sx={{ mb: 2.5 }}>
      <Typography
        variant="caption"
        fontWeight={700}
        color="text.secondary"
        sx={{ textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', mb: 1.5 }}
      >
        {title}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
        {platforms.map(platform => (
          <PlatformTile
            key={platform.id}
            platform={platform}
            selected={selectedPlatform === platform.id}
            onClick={setSelectedPlatform}
          />
        ))}
      </Box>
    </Box>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight={600}>
              Connect a Platform
            </Typography>
            <IconButton onClick={onClose} size={isMobile ? 'small' : 'medium'}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Connect platforms to publish content and manage reviews automatically.
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          {/* Social Posting */}
          <PlatformGroup title="Social Posting" platforms={SOCIAL_PLATFORMS} />

          {/* Review Management */}
          <Box sx={{ mb: 2.5 }}>
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', mb: 1.5 }}
            >
              Review Management
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              {/* Google — special tile */}
              <GoogleTile
                isConnected={isConnected('google')}
                hasPlaceId={hasPlaceId}
                onSetPlaceId={() => setShowPlaceIdInput(prev => !prev)}
                onConnectOAuth={() => {
                  const oauthPlatform = 'google';
                  const redirectUri   = `${window.location.origin}/oauth/callback/google`;

                  sessionStorage.setItem('oauth_platform', oauthPlatform);
                  if (businessId && businessName) {
                    sessionStorage.setItem('oauth_business_id',   businessId);
                    sessionStorage.setItem('oauth_business_name', businessName);
                    sessionStorage.setItem('oauth_return_path',
                      `/business/${businessId}/${businessName}`);
                  }

                  initiateOAuth({ platform: oauthPlatform, redirectUri })
                    .unwrap()
                    .then(result => {
                      if (result.authUrl) window.location.href = result.authUrl;
                    })
                    .catch(err => {
                      console.error('Google OAuth failed:', err);
                      alert('Failed to connect Google. Please try again.');
                    });
                }}
              />

              {/* Other review platforms */}
              {REVIEW_PLATFORMS.map(platform => (
                <PlatformTile
                  key={platform.id}
                  platform={platform}
                  selected={selectedPlatform === platform.id}
                  onClick={setSelectedPlatform}
                />
              ))}
            </Box>

            {/* Inline Place ID input — shown when Set Place ID clicked */}
            <Collapse in={showPlaceIdInput}>
              <Box
                sx={{
                  mt:           1.5,
                  p:            2,
                  border:       '1px solid',
                  borderColor:  'primary.light',
                  borderRadius: 2,
                  bgcolor:      'primary.50'
                }}
              >
                <Typography variant="caption" fontWeight={600} color="primary.main" sx={{ display: 'block', mb: 1 }}>
                  Google Place ID
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="e.g. ChIJN1t_tDeuEmsRUsoyG83frY4"
                  value={googlePlaceId}
                  onChange={e => { setGooglePlaceId(e.target.value); setPlaceIdSaved(false); }}
                  helperText={
                    <Typography
                      variant="caption"
                      component="a"
                      href="https://developers.google.com/maps/documentation/javascript/examples/places-placeid-finder"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: 'primary.main', textDecoration: 'none' }}
                    >
                      How to find your Place ID ↗
                    </Typography>
                  }
                  sx={{ mb: 1 }}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSavePlaceId}
                  disabled={!googlePlaceId.trim() || savingPlaceId || !businessId}
                  color={placeIdSaved ? 'success' : 'primary'}
                  startIcon={
                    savingPlaceId
                      ? <CircularProgress size={14} color="inherit" />
                      : placeIdSaved
                        ? <CheckIcon fontSize="small" />
                        : <SaveIcon fontSize="small" />
                  }
                >
                  {savingPlaceId ? 'Saving...' : placeIdSaved ? 'Saved!' : 'Save Place ID'}
                </Button>
              </Box>
            </Collapse>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={onClose} fullWidth={isMobile}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConnect}
            disabled={!selectedPlatform || isConnecting}
            startIcon={isConnecting ? <CircularProgress size={16} /> : <ArrowIcon />}
            fullWidth={isMobile}
          >
            {isConnecting ? 'Connecting...' : 'Continue'}
          </Button>
        </DialogActions>
      </Dialog>

      <PermissionsConsentDialog
        open={showPermissionsConsent}
        onClose={() => setShowPermissionsConsent(false)}
        onAccept={handlePermissionsAccepted}
        platform={selectedPlatform === 'facebook_instagram' ? 'facebook' : selectedPlatform}
      />
    </>
  );
};

export default OAuthConnectionDialog;